import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, map, forkJoin } from 'rxjs';
import { LookupValue } from '../models/lookup.model';
import { ODataResponse } from '../models/odata-response.model';
import { EnvironmentConfig, ENV_CONFIG } from '../../environments/environment.model';
import { BypassInformation, PigAccessory, PigAssociatedProduct, PigBulletin, PigComponent, PigQueryResult, ProductInformation, ReplacementPart } from '../models';

@Injectable({
  providedIn: 'root'
})
export class PigService {
  private url: string;
  private version: string;

  constructor(
    private http: HttpClient,
    @Inject(ENV_CONFIG) private config: EnvironmentConfig) {
    this.url = this.config.endpoints.baseApi.url;
    this.version = this.config.endpoints.baseApi.version;
  }

  private getDiameters(): Observable<LookupValue[]> {
    return this.http.get<ODataResponse<LookupValue[]>>(`${this.url}odata/${this.version}/PigDiameters`)
      .pipe(map(res => res.value || []));
  }
  private getPigEfficiencies(): Observable<LookupValue[]> {
    return this.http.get<ODataResponse<LookupValue[]>>(`${this.url}odata/${this.version}/PigEfficiencies`)
      .pipe(map(res => res.value || []));;
  }
  private getPigPipelineServices(): Observable<LookupValue[]> {
    return this.http.get<ODataResponse<LookupValue[]>>(`${this.url}odata/${this.version}/PigPipelineServices`)
      .pipe(map(res => res.value || []));
  }

  private getPigPurposes(): Observable<LookupValue[]> {
    return this.http.get<ODataResponse<LookupValue[]>>(`${this.url}odata/${this.version}/PigPiggingPurposes`)
      .pipe(map(res => res.value || []));
  }

  private getProductInformation(activePigId: number): Observable<ProductInformation[]> {
    return this.http.get<ProductInformation[]>(`${this.url}api/${this.version}/PigProducts/ProductInformation/${activePigId}`);
  }

  private getBypassInformation(activePigId: number): Observable<BypassInformation[]> {
    return this.http.get<BypassInformation[]>(`${this.url}api/${this.version}/PigProducts/BypassInformation/${activePigId}`);
  }

  private getReplacementParts(pigPartNumber: string): Observable<ReplacementPart[]> {
    return this.http.get<ReplacementPart[]>(`${this.url}api/${this.version}/PigProducts/ReplacementParts/${pigPartNumber}`);
  }

  private getPigComponents(activePigId: number): Observable<PigComponent[]> {
    return this.http.get<PigComponent[]>(`${this.url}api/${this.version}/PigProducts/Components/${activePigId}`);
  }

  private getAccessories(activePigId: number): Observable<PigAccessory[]> {
    return this.http.get<PigAccessory[]>(`${this.url}api/${this.version}/PigProducts/Accessories/${activePigId}`);
  }

  private getAssociateProducts(activePigId: number): Observable<PigAssociatedProduct[]> {
    return this.http.get<PigAssociatedProduct[]>(`${this.url}api/${this.version}/PigProducts/AssociatedProducts/${activePigId}`);
  }
  private getBulletin(familyId: number, purposeId: number): Observable<PigBulletin[]> {
    return this.http.get<PigBulletin[]>(`${this.url}api/${this.version}/PigProducts/Bulletin/${familyId}/${purposeId}`);
  }



  getLookups(): Observable<{
    diameters: LookupValue[],
    pigEfficiencies: LookupValue[],
    pipelineServices: LookupValue[],
    pigPurposes: LookupValue[]
  }> {
    const diameters = this.getDiameters();
    const pigEfficiencies = this.getPigEfficiencies();
    const pipelineServices = this.getPigPipelineServices();
    const pigPurposes = this.getPigPurposes();

    return forkJoin([diameters, pigEfficiencies, pipelineServices, pigPurposes]).pipe(
      map(([diameters, pigEfficiencies, pipelineServices, pigPurposes]) =>
        ({ diameters, pigEfficiencies, pipelineServices, pigPurposes })));
  }

  getQueryResults(queryString: string): Observable<PigQueryResult[]> {
    return this.http.get<PigQueryResult[]>(`${this.url}api/${this.version}/PigProducts${queryString}`);
  }

  getPigDetail(id: number): Observable<PigQueryResult & {
    associatedProducts: PigAssociatedProduct[],
    accessories: PigAccessory[],
    bulletin: PigBulletin,
    bypassInformation: BypassInformation[],
    components: PigComponent[],
    productInformation: ProductInformation[],
    replacementParts: ReplacementPart[]
  }> {
    return this.http.get<
      PigQueryResult & {
        associatedProducts: PigAssociatedProduct[],
        accessories: PigAccessory[],
        bulletin: PigBulletin,
        bypassInformation: BypassInformation[],
        components: PigComponent[],
        productInformation: ProductInformation[],
        replacementParts: ReplacementPart[]
      }>(`${this.url}api/${this.version}/PigProducts/${id}`);
  }

  getPigDetailRefs(
    activepigId: number,
    pigPartNumber: string,
    familyId: number,
    purposeId: number): Observable<{
      productInfo: ProductInformation[],
      bypassInfo: BypassInformation[],
      replacementParts: ReplacementPart[],
      components: PigComponent[],
      accessories: PigAccessory[],
      associatedProducts: PigAssociatedProduct[],
      bulletin: PigBulletin[]
    }> {
    return forkJoin([
      this.getProductInformation(activepigId),
      this.getBypassInformation(activepigId),
      this.getReplacementParts(pigPartNumber),
      this.getPigComponents(activepigId),
      this.getAccessories(activepigId),
      this.getAssociateProducts(activepigId),
      this.getBulletin(familyId, purposeId),
    ]).pipe(map(([productInfo, bypassInfo, replacementParts, components, accessories, associatedProducts, bulletin]) =>
      ({ productInfo, bypassInfo, replacementParts, components, accessories, associatedProducts, bulletin })));
  }

  getPigCutSheet(pigId: number): Observable<Blob> {
    return this.http.get<ArrayBuffer>(`${this.url}api/${this.version}/PigProducts/report/${pigId}`,
      { responseType: 'arraybuffer' as 'json' })
      .pipe(map(res => new Blob([new Uint8Array(res, 0, res.byteLength)], { type: 'application/pdf' })));
  }
}
